/**
 * 商品模板 → 发布产品：跨新标签传递当前表单快照。
 * 优先 IndexedDB（可容纳含图片的大表单）；失败再退回 localStorage。
 * 按 token 读取，短时内可重复 peek（兼容 React Strict Mode 双挂载）。
 */

export const PRODUCT_TEMPLATE_PUBLISH_HANDOFF_KEY = 'ipe.temu.publish.fromProductTemplate';
const HANDOFF_TTL_MS = 5 * 60 * 1000;
const IDB_NAME = 'ipe-temu-product-template-handoff';
const IDB_STORE = 'snapshots';
const IDB_VERSION = 1;

function makeToken() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function storageKey(token) {
  return `${PRODUCT_TEMPLATE_PUBLISH_HANDOFF_KEY}.${String(token || '').trim()}`;
}

function isFresh(at) {
  const ts = Number(at) || 0;
  return ts > 0 && Date.now() - ts <= HANDOFF_TTL_MS;
}

function openHandoffDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function idbPut(token, payload) {
  return openHandoffDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('indexedDB put failed'));
    };
    tx.objectStore(IDB_STORE).put(payload, token);
  }));
}

function idbGet(token) {
  return openHandoffDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(token);
    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error || new Error('indexedDB get failed'));
    };
  }));
}

function idbDelete(token) {
  return openHandoffDb().then((db) => new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        resolve();
      };
      tx.objectStore(IDB_STORE).delete(token);
    } catch {
      try { db.close(); } catch { /* ignore */ }
      resolve();
    }
  })).catch(() => {});
}

function writeLocalFallback(token, payload) {
  if (typeof localStorage === 'undefined') return false;
  try {
    localStorage.setItem(storageKey(token), JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

function readLocalFallback(token) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(token));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearLocalFallback(token) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(storageKey(token));
  } catch {
    /* ignore */
  }
}

/**
 * @returns {Promise<string|null>} handoff token；失败返回 null
 */
export async function writeProductTemplatePublishHandoff(form) {
  const token = makeToken();
  const payload = {
    form: form && typeof form === 'object' ? form : {},
    at: Date.now(),
  };
  try {
    await idbPut(token, payload);
    return token;
  } catch {
    if (writeLocalFallback(token, payload)) return token;
    return null;
  }
}

/**
 * 读取 handoff（不立即删除，便于 Strict Mode 二次挂载）。
 * @returns {Promise<object|null>}
 */
export async function readProductTemplatePublishHandoff(token) {
  const id = String(token || '').trim();
  if (!id || id === '1') return null;

  let payload = null;
  try {
    payload = await idbGet(id);
  } catch {
    payload = null;
  }
  if (!payload) payload = readLocalFallback(id);
  if (!payload || !isFresh(payload.at)) return null;
  if (!payload.form || typeof payload.form !== 'object') return null;
  return payload.form;
}

/** 延迟清理，避免新标签 Strict Mode 双挂载读空 */
export function scheduleClearProductTemplatePublishHandoff(token, delayMs = 15000) {
  const id = String(token || '').trim();
  if (!id || id === '1') return;
  const run = () => {
    clearLocalFallback(id);
    idbDelete(id);
  };
  if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
    window.setTimeout(run, delayMs);
  } else {
    run();
  }
}
